import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import { Upload, Download, FileText, Trash2, Eye } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import DocumentPreviewDrawer from '../DocumentPreviewDrawer';

export default function CandidateCvDialog({ open, onClose, candidato, onUpdated }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewFilename, setPreviewFilename] = useState('');
  const [previewItem, setPreviewItem] = useState(null);
  const [openInNewTabLoading, setOpenInNewTabLoading] = useState(false);

  const isPdf = (item) => String(item?.mime_type || '').toLowerCase().includes('pdf') || String(item?.original_name || '').toLowerCase().endsWith('.pdf');
  const isImage = (item) => String(item?.mime_type || '').toLowerCase().startsWith('image/');

  const formatBytes = (bytes) => {
    const n = Number(bytes || 0);
    if (!Number.isFinite(n) || n <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    let v = n;
    let i = 0;
    while (v >= 1024 && i < units.length - 1) {
      v /= 1024;
      i += 1;
    }
    return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
  };

  const load = async () => {
    if (!candidato) return;
    setLoading(true);
    try {
      const res = await api.get(
        `/media/entity/HR_CANDIDATO/${candidato.id}`,
        { params: { category_code: 'HR_CV' } }
      );
      const list = res.data || [];
      setItems(list);
      return list;
    } catch (error) {
      console.error('Error loading CV list:', error);
      toast.error('Errore nel caricamento dei curriculum');
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      load();
    }
  }, [open, candidato?.id]);

  const handleUploadChange = async (event) => {
    if (!candidato) return;
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    setUploading(true);
    try {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('files', file);
      });
      formData.append('category_code', 'HR_CV');
      formData.append('role', 'CV');

      await api.post(
        `/media/entity/HR_CANDIDATO/${candidato.id}/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Curriculum caricati correttamente');
      const list = await load();
      if (typeof onUpdated === 'function') {
        onUpdated({
          candidatoId: candidato.id,
          hasCurriculum: (list || []).length > 0
        });
      }
    } catch (error) {
      console.error('Error uploading CV:', error);
      toast.error('Errore nel caricamento dei curriculum');
    } finally {
      setUploading(false);
      event.target.value = null;
    }
  };

  const handleDownload = async (mediaId, filename) => {
    try {
      const response = await api.get(`/media/${mediaId}/download`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/octet-stream'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'curriculum';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading CV:', error);
      toast.error('Errore nel download del curriculum');
    }
  };

  const handlePreview = async (item) => {
    if (!item || !item.media_id) return;
    if (!isPdf(item) && !isImage(item)) {
      await handleDownload(item.media_id, item.original_name);
      return;
    }

    try {
      setPreviewItem(item);
      setPreviewFilename(item.original_name || 'curriculum');
      setPreviewOpen(true);
      setPreviewLoading(true);

      const presigned = await api.get(`/media/${item.media_id}/preview-url`);
      const url = presigned?.data?.url || '';
      if (url) {
        setPreviewUrl(url);
        return;
      }

      const response = await api.get(`/media/${item.media_id}/download`, { responseType: 'blob' });
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || item.mime_type || 'application/octet-stream'
      });
      const blobUrl = window.URL.createObjectURL(blob);
      setPreviewUrl(blobUrl);
    } catch (error) {
      console.error('Error previewing CV:', error);
      toast.error('Errore nell\'apertura del curriculum');
      setPreviewOpen(false);
      setPreviewFilename('');
      setPreviewItem(null);
      setPreviewUrl('');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDelete = async (linkId) => {
    if (!linkId) return;
    const confirm = window.confirm('Confermi la rimozione di questo curriculum?');
    if (!confirm) return;
    try {
      await api.delete(`/media/link/${linkId}`);
      toast.success('Curriculum rimosso');
      const list = await load();
      if (typeof onUpdated === 'function') {
        onUpdated({
          candidatoId: candidato?.id,
          hasCurriculum: (list || []).length > 0
        });
      }
    } catch (error) {
      console.error('Error deleting CV:', error);
      toast.error('Errore nella rimozione del curriculum');
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>
          Curriculum candidato
          {candidato && (
            <Typography variant="subtitle2" color="text.secondary">
              {candidato.cognome} {candidato.nome}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Box
            sx={{
              border: '1px dashed #e2e8f0',
              borderRadius: 2,
              p: 2,
              mb: 2,
              textAlign: 'center',
              bgcolor: '#f9fafb'
            }}
          >
            <Button
              component="label"
              startIcon={<Upload size={18} />}
              disabled={uploading}
            >
              Carica CV
              <input
                type="file"
                hidden
                multiple
                accept=".pdf,.doc,.docx,.odt"
                onChange={handleUploadChange}
              />
            </Button>
            <Typography
              variant="caption"
              display="block"
              sx={{ mt: 1, color: 'text.secondary' }}
            >
              Formato consigliato: PDF (aperto direttamente nell&apos;app)
            </Typography>
          </Box>

          {loading ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 4
              }}
            >
              <CircularProgress size={24} />
            </Box>
          ) : items.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Nessun curriculum caricato
            </Typography>
          ) : (
            <List dense>
              {items.map((item) => (
                <ListItem key={item.link_id}>
                  <FileText size={18} style={{ marginRight: 8 }} />
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">{item.original_name}</Typography>
                        {item.mime_type &&
                          item.mime_type.startsWith('application/pdf') && (
                            <Box
                              sx={{
                                borderRadius: '999px',
                                px: 1,
                                py: 0.25,
                                bgcolor: 'rgba(34,197,94,0.08)',
                                color: '#16a34a',
                                fontSize: 10,
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: 0.4
                              }}
                            >
                              PDF
                            </Box>
                          )}
                      </Box>
                    }
                    secondary={
                      item.created_at
                        ? new Date(item.created_at).toLocaleString()
                        : null
                    }
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title="Anteprima">
                      <span>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => handlePreview(item)}
                          sx={{ mr: 1 }}
                        >
                          <Eye size={18} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() =>
                        handleDownload(item.media_id, item.original_name)
                      }
                      sx={{ mr: 1 }}
                    >
                      <Download size={18} />
                    </IconButton>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => handleDelete(item.link_id)}
                    >
                      <Trash2 size={18} />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      <DocumentPreviewDrawer
        open={previewOpen}
        onClose={() => {
          if (previewUrl && String(previewUrl).startsWith('blob:')) {
            window.URL.revokeObjectURL(previewUrl);
          }
          setPreviewOpen(false);
          setPreviewUrl('');
          setPreviewFilename('');
          setPreviewItem(null);
        }}
        fileName={previewFilename}
        fileUrl={previewUrl}
        loading={previewLoading}
        details={[
          { label: 'Nome', value: previewItem?.original_name || '-' },
          { label: 'Tipo', value: 'Curriculum candidato' },
          {
            label: 'Formato',
            value: isPdf(previewItem) ? 'PDF' : isImage(previewItem) ? 'Immagine' : previewItem?.mime_type || '-'
          },
          { label: 'Dimensione', value: formatBytes(previewItem?.size_bytes) },
          {
            label: 'Caricato il',
            value: previewItem?.created_at ? new Date(previewItem.created_at).toLocaleString('it-IT') : '-'
          },
          {
            label: 'Candidato',
            value: candidato ? `${candidato.cognome || ''} ${candidato.nome || ''}`.trim() : '-'
          }
        ]}
        onOpenInNewTab={async () => {
          if (!previewItem?.media_id) return;
          try {
            setOpenInNewTabLoading(true);
            const presigned = await api.get(`/media/${previewItem.media_id}/preview-url`);
            const url = presigned?.data?.url || previewUrl || '';
            if (url) {
              window.open(url, '_blank', 'noopener,noreferrer');
              return;
            }
            if (previewUrl) {
              window.open(previewUrl, '_blank', 'noopener,noreferrer');
            }
          } catch (_e) {
            if (previewUrl) {
              window.open(previewUrl, '_blank', 'noopener,noreferrer');
            }
          } finally {
            setOpenInNewTabLoading(false);
          }
        }}
        openInNewTabLoading={openInNewTabLoading}
        onDownload={
          previewItem?.media_id
            ? () => handleDownload(previewItem.media_id, previewItem.original_name)
            : null
        }
      />
    </>
  );
}
